import { z } from 'zod';
import { PCMP_CHANNELS, PCMP_FAILURE_STAGES, PCMP_PAYMENT_TYPES } from './pcmpProposal.types';
import { pcmpCmpReportSchema } from './pcmpReport.types';
import {
  pcmpPageMetaSchema,
  pcmpQueueItemSchema,
  pcmpQueuePersonSchema,
  pcmpQueueSearchShape,
  pcmpSkippedProposalSchema,
  type PcmpQueueFilters,
} from './pcmpQueue.types';

// GET /pcmp/accounts/* (API LB-759).

/** The item tabs of `GET queue`. */
export const PCMP_ACCOUNTS_TABS = [
  'DISBURSEMENT_FAILED',
  'REVERIFIED',
  'RETURNED',
  'COMPLETED',
  'ON_HOLD',
] as const;
export type PcmpAccountsTab = (typeof PCMP_ACCOUNTS_TABS)[number];

export const PCMP_ACCOUNTS_BATCH_KINDS = ['ROUTE', 'URGENT'] as const;
export type PcmpAccountsBatchKind = (typeof PCMP_ACCOUNTS_BATCH_KINDS)[number];

/** PENDING: a batch with an item to disburse; DISBURSED: one with a paid item. */
export const PCMP_ACCOUNTS_BATCH_VIEWS = ['PENDING', 'DISBURSED'] as const;
export type PcmpAccountsBatchView = (typeof PCMP_ACCOUNTS_BATCH_VIEWS)[number];

/** The page's tabs, in legacy order: batch lists, item tabs, re-disbursement batches. */
export const PCMP_ACCOUNTS_VIEWS = [
  'PENDING_BATCHES',
  'DISBURSED_BATCHES',
  'URGENT',
  'COMPLETED',
  'DISBURSEMENT_FAILED',
  'RETURNED',
  'RETURN_REQUESTS',
  'REVERIFIED',
  'REDISBURSE_PENDING',
  'REDISBURSED',
  'ON_HOLD',
] as const;
export type PcmpAccountsView = (typeof PCMP_ACCOUNTS_VIEWS)[number];

export const PCMP_DISBURSEMENT_RESULTS = [
  'PENDING',
  'PAID',
  'FAILED',
  'RETURNED',
  'CANCELLED',
] as const;

const nullablePerson = pcmpQueuePersonSchema.nullable();

/** The proposal's latest payout attempt. */
export const pcmpAccountsAttemptSchema = z.object({
  disbursementId: z.coerce.number(),
  attemptNo: z.number(),
  result: z.enum(PCMP_DISBURSEMENT_RESULTS),
  amount: z.number(),
  batchNo: z.number().nullable(),
  batchKind: z.string().nullable(),
  paidAt: z.string().nullable(),
  paidBy: nullablePerson,
  failedAt: z.string().nullable(),
  failedBy: nullablePerson,
  failureStage: z.enum(PCMP_FAILURE_STAGES).nullable(),
  failureStageLabel: z.string().nullable(),
  failureReason: z.string().nullable(),
  returnedAt: z.string().nullable(),
  returnedBy: nullablePerson,
  returnReason: z.string().nullable(),
});

export type PcmpAccountsAttempt = z.infer<typeof pcmpAccountsAttemptSchema>;

export const pcmpAccountsQueueItemSchema = pcmpQueueItemSchema.extend({
  paymentSource: z.enum(PCMP_CHANNELS),
  paymentType: z.enum(PCMP_PAYMENT_TYPES),
  paymentTypeLabel: z.string(),
  /** Null until Accounts has paid or failed it. */
  lastAttempt: pcmpAccountsAttemptSchema.nullable(),
});

export type PcmpAccountsQueueItem = z.infer<typeof pcmpAccountsQueueItemSchema>;

export const paginatedPcmpAccountsQueueSchema = z.object({
  data: z.array(pcmpAccountsQueueItemSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpAccountsQueue = z.infer<typeof paginatedPcmpAccountsQueueSchema>;

export const pcmpAccountsQueueCountsSchema = z.object({
  pendingBatches: z.number(),
  disbursedBatches: z.number(),
  urgentPendingBatches: z.number(),
  disbursementFailed: z.number(),
  disbursementFailedAmount: z.number(),
  reverified: z.number(),
  reverifiedAmount: z.number(),
  redisbursePendingBatches: z.number(),
  redisbursedBatches: z.number(),
  returned: z.number(),
  returnedAmount: z.number(),
  completed: z.number(),
  onHold: z.number(),
});

export type PcmpAccountsQueueCounts = z.infer<typeof pcmpAccountsQueueCountsSchema>;

const amountSchema = z.object({ count: z.number(), amount: z.number() });

export type PcmpAccountsAmount = z.infer<typeof amountSchema>;

export const PCMP_ACCOUNTS_BATCH_ROLL_UPS = [
  'PENDING',
  'DISBURSEMENT_FAILED',
  'COMPLETED',
  'DISBURSED',
] as const;
export type PcmpAccountsBatchRollUp = (typeof PCMP_ACCOUNTS_BATCH_ROLL_UPS)[number];

/** A route or urgent batch, summed over its members routed to Accounts. */
export const pcmpAccountsBatchSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  kind: z.enum(PCMP_ACCOUNTS_BATCH_KINDS),
  publishedAt: z.string().nullable(),
  publishedBy: nullablePerson,
  itemCount: z.number(),
  totalAmount: z.number(),
  /** Held items are carved out of pending (legacy LB-323). */
  pending: amountSchema,
  disbursed: amountSchema,
  disbursementFailed: amountSchema,
  returned: amountSchema,
  held: amountSchema,
  /** Members matching the filters; null when none is set. */
  matchedItemCount: z.number().nullable(),
  rollUp: z.enum(PCMP_ACCOUNTS_BATCH_ROLL_UPS),
  rollUpLabel: z.string(),
});

export type PcmpAccountsBatch = z.infer<typeof pcmpAccountsBatchSchema>;

export const pcmpAccountsBatchesSchema = z.object({
  /** Over every batch in the list, not just this page. */
  totals: z.object({
    batches: z.number(),
    itemCount: z.number(),
    totalAmount: z.number(),
    pending: amountSchema,
    disbursed: amountSchema,
    disbursementFailed: amountSchema,
    returned: amountSchema,
    held: amountSchema,
  }),
  batches: z.object({ items: z.array(pcmpAccountsBatchSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpAccountsBatches = z.infer<typeof pcmpAccountsBatchesSchema>;

/** What the viewer may do to a batch item now; a reason when not. */
export const pcmpAccountsBatchItemSchema = pcmpAccountsQueueItemSchema.extend({
  canDisburse: z.boolean(),
  disburseBlockedReason: z.string().nullable(),
  canFail: z.boolean(),
  /** Before or after payout, when it may be failed. */
  failureStage: z.enum(PCMP_FAILURE_STAGES).nullable(),
  failBlockedReason: z.string().nullable(),
  canReturn: z.boolean(),
  returnBlockedReason: z.string().nullable(),
});

export type PcmpAccountsBatchItem = z.infer<typeof pcmpAccountsBatchItemSchema>;

export const pcmpAccountsPaymentTypeTotalSchema = amountSchema.extend({
  paymentType: z.enum(PCMP_PAYMENT_TYPES),
  paymentTypeLabel: z.string(),
});

export type PcmpAccountsPaymentTypeTotal = z.infer<typeof pcmpAccountsPaymentTypeTotalSchema>;

export const pcmpAccountsBatchProposalsSchema = z.object({
  /** The whole batch, whatever the filters narrow the rows to. */
  batch: pcmpAccountsBatchSchema,
  /** Filters narrow the members (and `showAll` is off). */
  isFiltered: z.boolean(),
  /** Pending, unheld items the filters match ("select all matching"); null without filters. */
  matchedPending: amountSchema.nullable(),
  /** The whole batch by payment type (legacy LDS-695). */
  paymentTypes: z.array(pcmpAccountsPaymentTypeTotalSchema),
  proposals: z.object({ items: z.array(pcmpAccountsBatchItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpAccountsBatchProposals = z.infer<typeof pcmpAccountsBatchProposalsSchema>;

/** A re-disbursement batch, summed over its payout attempts. */
export const pcmpRedisburseBatchSchema = z.object({
  batchId: z.coerce.number(),
  batchNo: z.number(),
  /** The batch the failed payouts first went out in. */
  parentBatchNo: z.number().nullable(),
  createdAt: z.string(),
  createdBy: nullablePerson,
  itemCount: z.number(),
  totalAmount: z.number(),
  pending: amountSchema,
  paid: amountSchema,
  failed: amountSchema,
  matchedItemCount: z.number().nullable(),
});

export type PcmpRedisburseBatch = z.infer<typeof pcmpRedisburseBatchSchema>;

export const paginatedPcmpRedisburseBatchesSchema = z.object({
  data: z.array(pcmpRedisburseBatchSchema),
  meta: pcmpPageMetaSchema,
});

export type PaginatedPcmpRedisburseBatches = z.infer<typeof paginatedPcmpRedisburseBatchesSchema>;

/** What the viewer may do to a re-disbursement batch member now (API LB-765). */
export const pcmpRedisburseBatchItemSchema = pcmpAccountsQueueItemSchema.extend({
  canRedisburse: z.boolean(),
  redisburseBlockedReason: z.string().nullable(),
  /** Before payout, while it waits in the batch. */
  canFail: z.boolean(),
  failureStage: z.enum(PCMP_FAILURE_STAGES).nullable(),
  failBlockedReason: z.string().nullable(),
});

export type PcmpRedisburseBatchItem = z.infer<typeof pcmpRedisburseBatchItemSchema>;

/** Live members only: one that failed out of the batch has left it. */
export const pcmpRedisburseBatchProposalsSchema = z.object({
  batch: pcmpRedisburseBatchSchema,
  isFiltered: z.boolean(),
  proposals: z.object({ items: z.array(pcmpRedisburseBatchItemSchema), meta: pcmpPageMetaSchema }),
});

export type PcmpRedisburseBatchProposals = z.infer<typeof pcmpRedisburseBatchProposalsSchema>;

/** The CMP Accounts disbursement report of a route or urgent batch (with Status, and Orig. Batch when urgent). */
export const pcmpAccountsBatchReportSchema = pcmpCmpReportSchema.extend({
  batch: pcmpAccountsBatchSchema,
});

export type PcmpAccountsBatchReport = z.infer<typeof pcmpAccountsBatchReportSchema>;

/** A re-disbursement batch's CMP report, of its pending or its paid members (titled by the API). */
export const pcmpRedisburseBatchReportSchema = pcmpCmpReportSchema.extend({
  batch: pcmpRedisburseBatchSchema,
  view: z.enum(PCMP_ACCOUNTS_BATCH_VIEWS),
});

export type PcmpRedisburseBatchReport = z.infer<typeof pcmpRedisburseBatchReportSchema>;

export interface PcmpAccountsPageParams extends PcmpQueueFilters {
  page: number;
  limit: number;
}

export interface PcmpAccountsQueueParams extends PcmpAccountsPageParams {
  tab: PcmpAccountsTab;
}

export interface PcmpAccountsBatchesParams extends PcmpAccountsPageParams {
  kind: PcmpAccountsBatchKind;
  view: PcmpAccountsBatchView;
}

export interface PcmpRedisburseBatchesParams extends PcmpAccountsPageParams {
  view: PcmpAccountsBatchView;
}

export interface PcmpAccountsBatchProposalsParams extends PcmpAccountsPageParams {
  showAll?: boolean | undefined;
}

export interface PcmpRedisburseBatchProposalsParams extends PcmpAccountsBatchProposalsParams {
  /** Members still to pay or paid; both when left out. */
  view?: PcmpAccountsBatchView | undefined;
}

export const pcmpAccountsQueueSearchSchema = z.object({
  tab: z.enum(PCMP_ACCOUNTS_VIEWS).optional().catch(undefined),
  /** The Urgent tab's batch view; Pending when left out. */
  urgentView: z.enum(PCMP_ACCOUNTS_BATCH_VIEWS).optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpAccountsQueueSearch = z.infer<typeof pcmpAccountsQueueSearchSchema>;

export const pcmpAccountsBatchSearchSchema = z.object({
  showAll: z.boolean().optional().catch(undefined),
  ...pcmpQueueSearchShape,
});

export type PcmpAccountsBatchSearch = z.infer<typeof pcmpAccountsBatchSearchSchema>;

export const pcmpRedisburseBatchSearchSchema = pcmpAccountsBatchSearchSchema.extend({
  view: z.enum(PCMP_ACCOUNTS_BATCH_VIEWS).optional().catch(undefined),
});

export type PcmpRedisburseBatchSearch = z.infer<typeof pcmpRedisburseBatchSearchSchema>;

export const pcmpRedisburseBatchPrintSearchSchema = z.object({
  view: z.enum(PCMP_ACCOUNTS_BATCH_VIEWS).catch('PENDING'),
});

// Batch actions (API LB-760, LB-761).

/** The picked ids, or every pending item of the batch (or matching the filters) minus exclusions. */
export type PcmpAccountsBatchSelection =
  | { proposalIds: number[] }
  | {
      selectAll: 'BATCH' | 'MATCHING';
      filters?: PcmpQueueFilters | undefined;
      excludedProposalIds: number[];
    };

export type PcmpAccountsDisbursePayload = PcmpAccountsBatchSelection & { comment: string };

export type PcmpAccountsBatchFailPayload = PcmpAccountsBatchSelection & { reason: string };

export const pcmpAccountsDisburseResultSchema = z.object({
  batchNo: z.number(),
  disbursed: z.array(z.coerce.number()),
  disbursedAmount: z.number(),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpAccountsDisburseResult = z.infer<typeof pcmpAccountsDisburseResultSchema>;

export const pcmpAccountsBatchFailResultSchema = z.object({
  batchNo: z.number(),
  failed: z.array(z.coerce.number()),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpAccountsBatchFailResult = z.infer<typeof pcmpAccountsBatchFailResultSchema>;

// Re-disbursement (API LB-765).

/** Corrected proposals by id, or every one the Reverified tab matches minus exclusions. */
export type PcmpRedisburseCreateSelection =
  | { proposalIds: number[] }
  | {
      selectAll: 'MATCHING';
      filters?: PcmpQueueFilters | undefined;
      excludedProposalIds: number[];
    };

export type PcmpRedisburseCreatePayload = PcmpRedisburseCreateSelection & { comment: string };

export const pcmpRedisburseCreateResultSchema = z.object({
  batchId: z.coerce.number(),
  /** 9000001 up. */
  batchNo: z.number(),
  /** The route or urgent batch every member came from; null when they differ. */
  parentBatchNo: z.number().nullable(),
  added: z.array(z.coerce.number()),
  addedAmount: z.number(),
  skipped: z.array(pcmpSkippedProposalSchema),
});

export type PcmpRedisburseCreateResult = z.infer<typeof pcmpRedisburseCreateResultSchema>;
